import dayjs from 'dayjs'
import type { BalanceSlot } from '@sportninja/common/services/teamBalance'
import type { BalanceSheetRow } from '../types/balanceGrid'
import { isCandidateTableRow, isOpenPlayerSlot } from './balanceRowKind'

/**
 * Jeff, Sep 24 — the sheet's DEFAULT row order: people in the order they joined.
 * The rep who paid at registration is #1, and the next player to join is #2.
 *
 * This supersedes two earlier rules. Inon's Aug 31 rule compacted the players to the
 * top so that `#` counted them 1..n, and Sep 23 put the team officials after the open
 * slots to keep that count. `#` is still just the visible row number (IndexCell); in
 * the default order it now counts the joined rows, players and officials' lines alike,
 * in the order they joined. The footer's "n of m filled" still counts players only.
 *
 * The server's order cannot give this. It sends slots by `team_registration_slot.id`,
 * and occupancy does not follow the ids: `attachPlayerToNextOpenSlot` fills the LOWEST
 * open slot in place and `releaseSlot` empties one where it stands. A player who joins
 * after an official's line was opened can therefore sit in an OLDER slot than the line.
 * So each row is placed by its occupant's own `joined_at` instead: a player's
 * registration, or when the official's line was opened.
 *
 * This is DISPLAY ONLY. Slot identity is the uid, never the row number, and every
 * column stays sortable — clicking a header still overrides this order the way it
 * always did.
 */
export const ROW_GROUP_JOINED = 0
export const ROW_GROUP_OPEN = 1
/**
 * A team official with no line yet ($0, "Show: All" only). After the open slots: this
 * person has not joined the budget. Typing an amount opens their line, which then sorts
 * by when it was opened.
 */
export const ROW_GROUP_CANDIDATE = 2
export const ROW_GROUP_CANCELLED = 3

/**
 * Which display band a slot belongs to: every filled player slot and every official
 * line (current or former) has joined; an empty player slot is still to fill.
 *
 * Cancelled is its own band rather than part of JOINED even though a cancelled slot
 * still has an occupant: the registration is dead, the slot is excluded from the
 * split, and it is hidden entirely unless "Display Cancelled" is on — so a withdrawn
 * player must not push a slot the rep can still fill further down the sheet. A
 * cancelled official line joins it for the same reason.
 *
 * PLAYER FINDER needs no case of its own: it is a display variant of `open` (see
 * `balanceFinder`), and an advertised slot IS a slot still to fill.
 */
export const slotRowGroup = (slot: BalanceSlot): number => {
  if (slot.status === 'cancelled') {
    return ROW_GROUP_CANCELLED
  }

  return isOpenPlayerSlot(slot) ? ROW_GROUP_OPEN : ROW_GROUP_JOINED
}

/** The band of any sheet row — a candidate is an official without a line. */
export const sheetRowGroup = (row: BalanceSheetRow): number =>
  isCandidateTableRow(row) ? ROW_GROUP_CANDIDATE : slotRowGroup(row.slot)

/**
 * When a row's occupant joined, in ms. A missing or unparseable time (an older api2,
 * an open slot) is +Infinity, so it sorts after every dated row — never NaN, which
 * would make the comparator inconsistent.
 */
export const joinedAtMs = (slot: Pick<BalanceSlot, 'joined_at'>): number => {
  if (!slot.joined_at) {
    return Number.POSITIVE_INFINITY
  }
  const parsed = dayjs(slot.joined_at)

  return parsed.isValid() ? parsed.valueOf() : Number.POSITIVE_INFINITY
}

/**
 * Earlier first. Compared, never subtracted: two undated rows are ∞ − ∞, which is
 * NaN, and a NaN from a comparator breaks the whole sort.
 */
const compareTimes = (left: number, right: number): number =>
  left < right ? -1 : left > right ? 1 : 0

/**
 * Candidates: by name, then by the person. Two rows of one person can only come from
 * the same payload twice, and then the incoming order decides.
 */
const compareCandidates = (a: BalanceSheetRow, b: BalanceSheetRow): number => {
  if (!isCandidateTableRow(a) || !isCandidateTableRow(b)) {
    return 0
  }

  return (
    a.candidate.name.localeCompare(b.candidate.name, undefined, {
      sensitivity: 'base',
    }) || a.candidate.payer_user_id.localeCompare(b.candidate.payer_user_id)
  )
}

interface DecoratedRow<T> {
  row: T
  index: number
  group: number
  joined: number
}

const compareWithinGroup = <T extends BalanceSheetRow>(
  a: DecoratedRow<T>,
  b: DecoratedRow<T>
): number => {
  switch (a.group) {
    case ROW_GROUP_JOINED:
    case ROW_GROUP_CANCELLED:
      return compareTimes(a.joined, b.joined)
    case ROW_GROUP_CANDIDATE:
      return compareCandidates(a.row, b.row)
    default:
      // Open slots keep the server's own order.
      return 0
  }
}

/**
 * Order one registration's rows for display, banded as above:
 *
 *   JOINED     `joined_at` ascending
 *   OPEN       the server's order
 *   CANDIDATE  name, then person
 *   CANCELLED  `joined_at` ascending
 *
 * Every tie keeps the incoming (server) order: two players who joined in the same
 * second, rows with no time, and the open slots. The opaque slot uid is never
 * compared.
 *
 * Decorate-sort-undecorate rather than a bare `.sort()`: the time is parsed once per
 * row rather than once per comparison, and the incoming index is stated as the final
 * tie-break instead of leaning on sort stability.
 *
 * Returns a new array — the input's slots belong to the react-query cache.
 */
export const orderSheetRows = <T extends BalanceSheetRow>(rows: T[]): T[] =>
  rows
    .map(
      (row, index): DecoratedRow<T> => ({
        row,
        index,
        group: sheetRowGroup(row),
        joined: isCandidateTableRow(row)
          ? Number.POSITIVE_INFINITY
          : joinedAtMs(row.slot),
      })
    )
    .sort(
      (a, b) =>
        a.group - b.group || compareWithinGroup(a, b) || a.index - b.index
    )
    .map(({ row }) => row)
